import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import {
  createInventory,
  updateInventory,
  fetchInventory,
  getInventories,
  acceptInventory,
  acceptInventoryBulk,
  rejectInventory,
  rejectInventoryBulk,
  generateAcceptanceAct,
  getEquipmentInventories,
} from '../../services/inventory'

export const createInventoryAction = createAsyncThunk(
  'inventory/create',
  async ({ equipment, project, type, stockroom, comment, sale_price }, { rejectWithValue }) => {
    try {
      const response = await createInventory({
        data: {
          data: {
            equipment,
            target_project: project,
            operation_type: type,
            target_stockroom: stockroom,
            comment,
            sale_price,
          },
        },
      })
      return response.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

export const createInventoriesAction = createAsyncThunk(
  'inventory/createBulk',
  async ({ equipments, project, type, stockroom, comment, sale_price }, { rejectWithValue }) => {
    try {
      const response = await createInventory({
        data: {
          data: equipments.map((equipment) => ({
            equipment,
            target_project: project,
            operation_type: type,
            target_stockroom: stockroom,
            comment,
            sale_price,
          })),
        },
      })
      return response.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

export const updateInventoryAction = createAsyncThunk(
  'inventory/update',
  async ({ id, attachment }, { rejectWithValue }) => {
    try {
      const response = await updateInventory(id, {
        data: { id, attachment },
      })
      return response.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

export const fetchInventoryByIdAction = createAsyncThunk('inventory/load', async (id, { rejectWithValue }) => {
  try {
    const data = await fetchInventory(id)
    return data.data
  } catch (e) {
    return rejectWithValue(e.response.data)
  }
})

export const getInventoriesAction = createAsyncThunk(
  'inventory/get',
  async ({ filters, pagination }, { rejectWithValue }) => {
    try {
      const response = await getInventories(filters, pagination)
      return response.data
    } catch (e) {
      return rejectWithValue(e?.response?.data)
    }
  }
)

export const getEquipmentInventoriesAction = createAsyncThunk(
  'inventory/equipment/get',
  async (equipment, { rejectWithValue }) => {
    try {
      const response = await getEquipmentInventories(equipment)
      return { equipment, data: response.data }
    } catch (e) {
      return rejectWithValue(e?.response?.data)
    }
  }
)

export const acceptInventoryAction = createAsyncThunk('inventory/accept', async (inventory, { rejectWithValue }) => {
  try {
    const response = await acceptInventory(inventory)
    return response.data
  } catch (e) {
    return rejectWithValue(e.response.data)
  }
})

export const acceptInventoryBulkAction = createAsyncThunk('inventory/acceptBulk', async (keys, { rejectWithValue }) => {
  try {
    const response = await acceptInventoryBulk({ keys })
    return response.data
  } catch (e) {
    return rejectWithValue(e.response.data)
  }
})

export const rejectInventoryAction = createAsyncThunk('inventory/reject', async (inventory, { rejectWithValue }) => {
  try {
    const response = await rejectInventory(inventory)
    return response.data
  } catch (e) {
    return rejectWithValue(e.response.data)
  }
})

export const rejectInventoryBulkAction = createAsyncThunk('inventory/rejectBulk', async (keys, { rejectWithValue }) => {
  try {
    const response = await rejectInventoryBulk({ keys })
    return response.data
  } catch (e) {
    return rejectWithValue(e.response.data)
  }
})

export const generateInventoryAcceptanceAct = createAsyncThunk(
  'inventory/generate/act',
  async (inventory, { rejectWithValue }) => {
    try {
      const response = await generateAcceptanceAct(inventory)
      return response.data
    } catch (e) {
      return rejectWithValue(e.response.data)
    }
  }
)

export const inventorySlice = createSlice({
  name: 'inventory',
  initialState: {
    inventories: [],
    pagination: {},
    equipmentInventories: {},
    isFetching: false,
    isSuccess: false,
    isError: false,
    errorMessage: '',
  },
  extraReducers: {
    [getInventoriesAction.pending]: (state) => {
      state.isFetching = true
    },
    [getInventoriesAction.rejected]: (state, error) => {
      state.isFetching = false
      state.isError = true
      state.errorMessage = error?.payload.message
    },
    [getInventoriesAction.fulfilled]: (state, data) => {
      state.inventories = data.payload.data
      state.pagination = data.payload.meta.pagination
      state.isFetching = false
      state.isSuccess = true
      return state
    },
    [getEquipmentInventoriesAction.fulfilled]: (state, { payload: { equipment, data } }) => {
      state.equipmentInventories = { ...state.equipmentInventories, [equipment]: data.data }
      return state
    },
  },
})

export const inventoriesSelector = (state) => state.inventories
